import { useState, useEffect, useRef, useCallback } from 'react';
import { App, Tooltip } from 'antd';
import { Download, ZoomIn, ZoomOut, FileText } from 'lucide-react';
import type { PdfPage, PdfTextItem } from '../utils/pdfRenderer';
import { renderPdf, mergeTextItemsInLine } from '../utils/pdfRenderer';
import { exportHtmlToPdf, downloadPdf } from '../utils/htmlToPdf';

interface PdfEditorProps {
  file: File;
  onClose: () => void;
}

// 编辑状态
interface EditState {
  [key: string]: string;
}

export default function PdfEditor({ file, onClose }: PdfEditorProps) {
  const { message } = App.useApp();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState<PdfPage[]>([]);
  const [scale, setScale] = useState(1);
  const [edits, setEdits] = useState<EditState>({});
  const [activeEditId, setActiveEditId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // 加载PDF
  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        const arrayBuffer = await file.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);

        // 验证PDF
        const header = new TextDecoder().decode(bytes.slice(0, 5));
        if (!header.startsWith('%PDF-')) {
          throw new Error('无效的PDF文件');
        }

        // 渲染PDF页面
        // 使用 2 倍缩放以获得更清晰的图片
        const renderedPages = await renderPdf(bytes, 2);
        setPages(renderedPages);

        setLoading(false);
      } catch (error) {
        console.error('加载PDF失败:', error);
        message.error(
          'PDF加载失败: ' +
            (error instanceof Error ? error.message : '未知错误'),
        );
        setLoading(false);
      }
    };

    loadPdf();
  }, [file, message]);

  // 处理文本编辑
  const handleTextChange = useCallback((id: string, newText: string) => {
    setEdits((prev) => ({ ...prev, [id]: newText }));
  }, []);

  // 导出PDF
  const handleExport = async () => {
    if (!containerRef.current || pages.length === 0) return;

    try {
      setExporting(true);
      message.loading('正在生成PDF...', 0);

      // 重新渲染PDF（不带白色遮块）
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      // 使用 2 倍缩放以获得更清晰的图片
      const originalPages = await renderPdf(bytes, 2, false); // false = 不移除文字

      // 创建导出容器
      const exportContainer = document.createElement('div');
      exportContainer.style.cssText = `
        position: fixed;
        left: -9999px;
        top: 0;
        width: ${originalPages[0].width}px;
      `;
      document.body.appendChild(exportContainer);

      // 为每页创建导出元素
      originalPages.forEach((page) => {
        const pageDiv = document.createElement('div');
        pageDiv.style.cssText = `
          width: ${page.width}px;
          height: ${page.height}px;
          position: relative;
          background: white;
          margin-bottom: 20px;
        `;

        // 添加Canvas图片 - 使用原始PDF渲染（不带白色遮块）
        // 使用 JPEG 格式，质量 0.95 以获得更清晰的图片
        if (page.canvas) {
          const img = document.createElement('img');
          img.src = page.canvas.toDataURL('image/jpeg', 0.95);
          img.style.cssText = `
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
          `;
          pageDiv.appendChild(img);
        }

        // 添加编辑后的文本
        page.textItems.forEach((item) => {
          const editedText = edits[item.id];
          if (editedText && editedText !== item.text) {
            const textDiv = document.createElement('div');
            textDiv.textContent = editedText;
            textDiv.style.cssText = `
              position: absolute;
              left: ${item.x}px;
              top: ${item.y}px;
              font-size: ${item.fontSize}px;
              font-family: ${item.fontName}, sans-serif;
              color: black;
              background: white;
              padding: 2px 4px;
              white-space: pre;
            `;
            pageDiv.appendChild(textDiv);
          }
        });

        exportContainer.appendChild(pageDiv);
      });

      // 导出为PDF
      // 使用 JPEG 格式，高质量以保持清晰度
      const blob = await exportHtmlToPdf(exportContainer, {
        filename: file.name.replace('.pdf', '_edited.pdf'),
        scale: 1, // canvas 已经是渲染好的图片，不需要额外缩放
        margin: 0,
        imageFormat: 'jpeg',
        imageQuality: 0.95, // 高质量以保持清晰度
      });

      // 清理
      document.body.removeChild(exportContainer);

      downloadPdf(blob, file.name.replace('.pdf', '_edited.pdf'));
      message.destroy();
      message.success('PDF导出成功');
    } catch (error) {
      console.error('导出PDF失败:', error);
      message.destroy();
      message.error('导出PDF失败');
    } finally {
      setExporting(false);
    }
  };

  // 放大
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 2));
  };

  // 缩小
  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  // 渲染文本编辑层
  const renderTextLayer = (page: PdfPage) => {
    // 合并同一行的文本
    const mergedItems = mergeTextItemsInLine(page.textItems);

    return (
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          width: page.width,
          height: page.height,
        }}
      >
        {mergedItems.map((item) => {
          const displayText = edits[item.id] ?? item.text;
          const isEditing = activeEditId === item.id;
          const isModified = edits[item.id] && edits[item.id] !== item.text;

          // 过滤掉页眉页脚区域
          if (item.y < 30 || item.y > page.height - 30) return null;

          return (
            <div
              key={item.id}
              className={`absolute pointer-events-auto cursor-text transition-all duration-150 px-1.5 py-0.5 rounded border ${
                isEditing
                  ? 'bg-blue-500/15 border-blue-500 ring-2 ring-blue-500/20 z-10 text-slate-800'
                  : isModified
                    ? 'bg-amber-500/10 border-amber-500/40 hover:bg-amber-500/15'
                    : 'border-transparent hover:bg-blue-500/10 hover:border-blue-500/30'
              }`}
              style={{
                left: item.x,
                top: item.y,
                minWidth: item.width,
                minHeight: item.height,
                fontSize: item.fontSize,
                fontFamily: `${item.fontName}, sans-serif`,
                fontWeight: 'normal',
                lineHeight: '1.2',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                caretColor: '#3b82f6',
              }}
              contentEditable
              suppressContentEditableWarning
              onFocus={() => setActiveEditId(item.id)}
              onBlur={(e) => {
                handleTextChange(item.id, e.currentTarget.textContent || '');
                setActiveEditId(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              onInput={(e) => {
                // 强制移除所有加粗标签，确保文本样式统一
                const target = e.currentTarget;
                if (target.querySelector('b, strong')) {
                  target.innerHTML = target.textContent || '';
                }
              }}
            >
              {displayText}
              {isModified && !isEditing && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full ring-2 ring-white" />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // 渲染页面
  const renderPage = (page: PdfPage) => {
    return (
      <div
        key={page.pageNum}
        className="relative bg-white mx-auto mb-8 rounded-sm shadow-[0_25px_50px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/5 transition-all duration-300 hover:shadow-[0_30px_60px_-12px_rgba(0,0,0,0.7)] hover:ring-amber-500/20"
        style={{
          width: page.width,
          height: page.height,
        }}
      >
        {/* Canvas 图片层 - 保留原始视觉效果 */}
        {page.canvas && (
          <img
            src={page.canvas.toDataURL('image/jpeg', 0.98)}
            alt={`Page ${page.pageNum}`}
            className="absolute inset-0 w-full h-full object-contain"
            style={{ imageRendering: 'crisp-edges' }}
          />
        )}

        {/* 文本编辑层 */}
        {renderTextLayer(page)}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="w-12 h-12 border-3 border-slate-700 border-t-amber-500 rounded-full animate-spin" />
        <p className="text-slate-400 mt-4 text-sm">正在解析PDF...</p>
      </div>
    );
  }

  const modifiedCount = Object.entries(edits).filter(([id, text]) => {
    const item = pages.flatMap((p) => p.textItems).find((i) => i.id === id);
    return item && text !== item.text;
  }).length;

  return (
    <div className="flex flex-col h-full">
      {/* 工具栏 */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-700 flex-shrink-0">
        {/* 左侧修改计数 */}
        <div className="flex items-center gap-3">
          {modifiedCount > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/15 border border-amber-500/30 rounded-full text-xs font-medium text-amber-400">
              <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
              {modifiedCount} 处修改
            </span>
          )}
        </div>

        {/* 中间控制区 */}
        <div className="flex items-center gap-3">
          {/* 页数显示 */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-lg border border-slate-700 text-slate-400 text-sm">
            <span>📄</span>
            <span>共</span>
            <span className="text-slate-200 font-medium">{pages.length}</span>
            <span>页</span>
          </div>

          <div className="w-px h-6 bg-slate-600" />

          {/* 缩放控制 */}
          <div className="flex items-center gap-1 bg-slate-800 rounded-lg px-2 border border-slate-700 h-[34px]">
            <Tooltip title="缩小">
              <button
                onClick={handleZoomOut}
                disabled={scale <= 0.5}
                className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-100 hover:bg-slate-700 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
            </Tooltip>
            <span className="min-w-[40px] text-center text-sm font-medium text-slate-100 tabular-nums">
              {Math.round(scale * 100)}%
            </span>
            <Tooltip title="放大">
              <button
                onClick={handleZoomIn}
                disabled={scale >= 2}
                className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-100 hover:bg-slate-700 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* 右侧导出按钮 */}
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 disabled:from-slate-700 disabled:to-slate-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm font-semibold shadow-lg shadow-amber-500/30 transition-all cursor-pointer"
        >
          <Download className="w-4 h-4" />
          {exporting ? '导出中...' : '导出PDF'}
        </button>
      </div>

      {/* 编辑区域 */}
      <div
        className="flex-1 overflow-auto p-6 relative bg-[#0a0f1c]"
        style={{
          background: `
            radial-gradient(circle at 20% 50%, rgba(99, 102, 241, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(79, 70, 229, 0.03) 0%, transparent 50%),
            #0a0f1c
          `,
        }}
      >
        {/* 网格背景 */}
        <div
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />

        <div
          ref={containerRef}
          className="flex flex-col items-center relative z-10"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top center',
            transition: 'transform 0.2s ease',
          }}
        >
          {pages.map(renderPage)}
        </div>
      </div>
    </div>
  );
}
